import { finite, value } from './operationalUi';

interface ChangeInput {
  current: number | null;
  prior: number | null;
  difference: number | null;
  changePct: number | null;
}

/** Percentage points for conversion rates; relative changes require a positive previous value. */
export function previousRunChange(metric: ChangeInput | undefined, unit: string) {
  if (!metric || !finite(metric.current) || !finite(metric.prior) || !finite(metric.difference))
    return { absolute: 'Unknown', relative: null };
  const sign = metric.difference > 0 ? '+' : '';
  const absolute = ['percent', 'ratio'].includes(unit)
    ? `${sign}${(metric.difference * 100).toLocaleString('en-AU', { maximumFractionDigits: Math.abs(metric.difference * 100) < 0.01 ? 4 : 2 })} pp`
    : `${sign}${value(metric.difference, unit)}`;
  const relative =
    metric.prior > 0 && finite(metric.changePct)
      ? `${metric.changePct > 0 ? '+' : ''}${value(metric.changePct, 'percent')}`
      : null;
  return { absolute, relative };
}

export function previousRunReason(reason: string | null | undefined) {
  if (!reason) return 'No paired evidence is available for this measure.';
  const labels: Record<string, string> = {
    identity_ambiguous: 'Property mapping is ambiguous; comparison withheld.',
    missing_stable_identity: 'No reliable property or offer identity is available.',
    no_previous_run_in_history: 'No earlier matching Flash run is retained in this history.',
    ambiguous_previous_run: 'More than one run has the latest earlier launch; comparison withheld.',
    previous_identity_ambiguous: 'The earlier run has an ambiguous property mapping.',
    overlapping_previous_run: 'The immediately earlier run overlaps this launch; comparison withheld.',
    absolute_observation_window_incompatible: 'Clear observation dates to compare the same launch days in both runs.',
    no_shared_launch_age: 'The runs have no comparable launch-day window.',
    market_impressions_unavailable: 'Impressions are unavailable for the selected website market.',
    missing_source_both: 'Both runs lack source records for this measure.',
    missing_source_current: 'The current run lacks source records for this measure.',
    missing_source_previous: 'The earlier run lacks source records for this measure.',
    zero_denominator_both: 'Both runs have a zero denominator for this measure.',
    zero_denominator_current: 'The current run has a zero denominator for this measure.',
    zero_denominator_previous: 'The earlier run has a zero denominator for this measure.',
    no_source_valid_metrics: 'These runs lack enough paired source evidence for a comparison.',
    too_early: 'Fewer than seven aligned days; excluded from portfolio totals.',
    too_early_for_summary: 'Fewer than seven aligned days; excluded from portfolio totals.',
  };
  if (labels[reason]) return labels[reason];
  return reason.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase());
}

export function previousRunValue(amount: number | null | undefined, unit: string) {
  return finite(amount) && ['percent', 'ratio'].includes(unit)
    ? `${(amount * 100).toLocaleString('en-AU', { maximumFractionDigits: Math.abs(amount * 100) < 1 ? 4 : 2 })}%`
    : value(amount, unit);
}
